import { useMemo } from "react";
import { cloneDeep } from "lodash";
import type { SearchDataModel, Types } from "@/entities/data-model";
import {
    FOCUS_ID_TO_NAME,
    FOCUS_NAME_TO_LOCATIONS,
    FormLevelFeatureType,
    type FormMetadataResponse,
} from "@/entities/form-metadata";
import { isDirectSearchPreferences, useUserPreferencesQuery } from "@/entities/user-preferences";
import { LETTER_TO_TYPE_MAPPING } from "./categoryBucket";
import { prepareGivenNameWithUPS, prepareLastNameWithUPS } from "./userPreferences";

interface UseUserPreferencesParams {
    dataModel: SearchDataModel;
    formMetadata: FormMetadataResponse;
    isEditForm?: boolean;
}

export const useUserPreferences = ({
    dataModel,
    formMetadata,
    isEditForm = false,
}: UseUserPreferencesParams): SearchDataModel => {
    const { data: userPreference } = useUserPreferencesQuery();

    return useMemo(() => {
        if (isEditForm || !userPreference) return dataModel;

        const dataModelToBeUpd = cloneDeep(dataModel);
        const collectionFocusFeature = formMetadata.features.find(
            (feature) => feature.featureType === FormLevelFeatureType.CollectionFocus,
        );
        const categoryBucketFeature = formMetadata.features.find(
            (feature) => feature.featureType === FormLevelFeatureType.CategoryBuckets,
        );
        const showUnviewedRecordsOnlyFeature = formMetadata.features.find(
            (feature) => feature.featureType === FormLevelFeatureType.ShowUnviewedRecordsOnly,
        );

        if (isDirectSearchPreferences(userPreference)) {
            // The direct route is already aligned with SearchDataModel: do not convert it through the legacy envelope.
            if (userPreference.givenNameExactness) {
                dataModelToBeUpd.name.givenNameExactness = userPreference.givenNameExactness;
            }
            if (userPreference.surnameExactness) {
                dataModelToBeUpd.name.surnameExactness = userPreference.surnameExactness;
            }

            if (dataModel.priority === "default" && collectionFocusFeature) {
                const validValues = collectionFocusFeature.attributes.limitedValues.map(
                    (option: { value: string }) => option.value,
                );
                const isValid = (value: string) => value === "default" || validValues.includes(value);
                const chosen =
                    (userPreference.collectionFocus && isValid(userPreference.collectionFocus)
                        ? userPreference.collectionFocus
                        : null) ??
                    (isValid(userPreference.defaultCollectionFocus) ? userPreference.defaultCollectionFocus : null);

                if (chosen && chosen !== "default") {
                    dataModelToBeUpd.priority = chosen;
                    dataModelToBeUpd.location = FOCUS_NAME_TO_LOCATIONS[chosen] ?? [];
                }
            }

            if (categoryBucketFeature) {
                dataModelToBeUpd.types = userPreference.types;
            }

            if (userPreference.showUnviewedRecordsOnly && showUnviewedRecordsOnlyFeature) {
                dataModelToBeUpd.showUnviewedRecordsOnly = userPreference.showUnviewedRecordsOnly;
            }
            return dataModelToBeUpd;
        }

        const userPreferences = userPreference.UserPreferences;
        if (userPreferences.FirstNameExactness) {
            dataModelToBeUpd.name.givenNameExactness = prepareGivenNameWithUPS(userPreferences.FirstNameExactness);
        }

        if (userPreferences.LastNameExactness) {
            dataModelToBeUpd.name.surnameExactness = prepareLastNameWithUPS(userPreferences.LastNameExactness);
        }

        // Collection Focus: URL wins > valid UPS > valid configured default > default.
        if (dataModel.priority === "default" && collectionFocusFeature) {
            const validValues = collectionFocusFeature.attributes.limitedValues.map(
                (option: { value: string }) => option.value,
            );
            const isValid = (value: string) => value === "default" || validValues.includes(value);
            const savedFocus = FOCUS_ID_TO_NAME[userPreferences.CollectionFocus] ?? userPreferences.CollectionFocus;
            const defaultFocus =
                FOCUS_ID_TO_NAME[userPreference.DefaultCollectionFocus] ?? userPreference.DefaultCollectionFocus;
            const chosen =
                (savedFocus && isValid(savedFocus) ? savedFocus : null) ??
                (defaultFocus && isValid(defaultFocus) ? defaultFocus : null);

            if (chosen && chosen !== "default") {
                dataModelToBeUpd.priority = chosen;
                dataModelToBeUpd.location = FOCUS_NAME_TO_LOCATIONS[chosen] ?? [];
            }
        }

        if (userPreferences.CategoryBucket && categoryBucketFeature) {
            dataModelToBeUpd.types = {
                records: false,
                photos: false,
                stories: false,
                trees: false,
            };
            userPreferences.CategoryBucket.split("").forEach((letter) => {
                const typeKey = LETTER_TO_TYPE_MAPPING[letter.toLowerCase()];
                if (typeKey && categoryBucketFeature.attributes.buckets[typeKey]?.show) {
                    dataModelToBeUpd.types[typeKey as keyof Types] = true;
                }
            });
        }

        if (userPreferences.ShowUnviewedRecordsOnly && showUnviewedRecordsOnlyFeature) {
            dataModelToBeUpd.showUnviewedRecordsOnly = userPreferences.ShowUnviewedRecordsOnly;
        }
        return dataModelToBeUpd;
    }, [dataModel, formMetadata, isEditForm, userPreference]);
};
